import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { Heart, LogOut, Mail, CalendarDays, Bell, Settings2, ArrowRight } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useWishlist } from "@/lib/wishlist";
import { usePriceAlerts } from "@/lib/price-alerts";
import { SUPPORTED_CURRENCIES, SUPPORTED_LOCALES, useSettings, type Currency, type Locale } from "@/lib/settings";

export const Route = createFileRoute("/_site/account/")({
  head: () => ({
    meta: [
      { title: "Your account — NexKey.gg" },
      { name: "description", content: "Manage your NexKey account, wishlist and price alerts." },
      { property: "og:title", content: "Your account — NexKey.gg" },
      { property: "og:description", content: "Manage your NexKey account, wishlist and price alerts." },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: AccountPage,
});

const LOCALE_LABEL: Record<Locale, string> = { en: "English", de: "Deutsch", es: "Español", pl: "Polski" };

function AccountPage() {
  const { t, locale, currency, theme, setLocale, setCurrency, setTheme } = useSettings();
  const { user, loading, logout } = useAuth();
  const { ids, ready } = useWishlist();
  const alerts = usePriceAlerts();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) navigate({ to: "/account/login", replace: true });
  }, [loading, user, navigate]);

  if (loading || !user) {
    return (
      <div className="container mx-auto px-4 py-16 text-sm text-muted-foreground">{t("account.loading")}</div>
    );
  }

  const created = user.createdAt
    ? new Date(user.createdAt).toLocaleDateString(locale === "en" ? "en-GB" : locale)
    : null;
  const activeAlerts = alerts.alerts.filter((a) => a.active).length;

  return (
    <div className="container mx-auto max-w-4xl px-4 py-12">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold">{t("account.title")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t("account.subtitle")}</p>
        </div>
        <button
          type="button"
          onClick={() => {
            logout();
            navigate({ to: "/account/login", replace: true });
          }}
          className="inline-flex items-center gap-2 rounded-md border border-border bg-surface px-4 py-2 text-sm font-semibold hover:border-primary/60"
        >
          <LogOut className="h-4 w-4" /> {t("account.logout")}
        </button>
      </div>

      {/* Profile */}
      <div className="mt-6 rounded-2xl border border-border bg-card p-6">
        <div className="flex items-center gap-3 text-sm">
          <Mail className="h-4 w-4 text-primary" />
          <span className="font-medium break-all">{user.email}</span>
        </div>
        {created && (
          <div className="mt-3 flex items-center gap-3 text-sm text-muted-foreground">
            <CalendarDays className="h-4 w-4" />
            {t("account.memberSince")}: {created}
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <Link
          to="/wishlist"
          className="group relative overflow-hidden rounded-2xl border border-border bg-card p-6 transition hover:border-primary/60 hover:shadow-glow"
        >
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/60 to-transparent" />
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              {t("account.dashboard.wishlist")}
            </span>
            <Heart className="h-4 w-4 text-primary" />
          </div>
          <div className="mt-2 font-display text-4xl font-black">{ready ? ids.length : "…"}</div>
          <div className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-primary">
            {t("account.dashboard.viewWishlist")} <ArrowRight className="h-3 w-3 transition group-hover:translate-x-0.5" />
          </div>
        </Link>
        <Link
          to="/account/price-alerts"
          className="group relative overflow-hidden rounded-2xl border border-border bg-card p-6 transition hover:border-primary/60 hover:shadow-glow"
        >
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/60 to-transparent" />
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              {t("account.dashboard.alerts")}
            </span>
            <Bell className="h-4 w-4 text-primary" />
          </div>
          <div className="mt-2 font-display text-4xl font-black">{alerts.isLoading ? "…" : activeAlerts}</div>
          <div className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-primary">
            {t("account.dashboard.viewAlerts")} <ArrowRight className="h-3 w-3 transition group-hover:translate-x-0.5" />
          </div>
        </Link>
      </div>

      {/* Settings */}
      <div className="mt-4 rounded-2xl border border-border bg-card p-6">
        <h2 className="flex items-center gap-2 font-display text-lg font-bold">
          <Settings2 className="h-4 w-4 text-primary" /> {t("account.settings")}
        </h2>
        <p className="mt-1 text-xs text-muted-foreground">{t("account.settings.hint")}</p>

        <div className="mt-5 grid gap-4 sm:grid-cols-3">
          <label className="block">
            <span className="text-xs font-medium text-muted-foreground">{t("account.settings.language")}</span>
            <select
              value={locale}
              onChange={(e) => setLocale(e.target.value as Locale)}
              className="mt-1 w-full rounded-md border border-border bg-surface px-3 py-2 text-sm outline-none focus:border-primary"
            >
              {SUPPORTED_LOCALES.map((l) => (
                <option key={l} value={l}>{LOCALE_LABEL[l]}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-medium text-muted-foreground">{t("account.settings.currency")}</span>
            <select
              value={currency}
              onChange={(e) => setCurrency(e.target.value as Currency)}
              className="mt-1 w-full rounded-md border border-border bg-surface px-3 py-2 text-sm outline-none focus:border-primary"
            >
              {SUPPORTED_CURRENCIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-medium text-muted-foreground">{t("account.settings.theme")}</span>
            <select
              value={theme}
              onChange={(e) => setTheme(e.target.value as "dark" | "light")}
              className="mt-1 w-full rounded-md border border-border bg-surface px-3 py-2 text-sm outline-none focus:border-primary"
            >
              <option value="dark">{t("account.settings.dark")}</option>
              <option value="light">{t("account.settings.light")}</option>
            </select>
          </label>
        </div>
        <p className="mt-4 text-[11px] text-muted-foreground">{t("account.settings.soon")}</p>
      </div>
    </div>
  );
}
